import React, {
  FC,
  useMemo,
  useState,
} from 'react';
import clsx from 'clsx';
import NavigationTabs from 'pages/Dashboard/components/NavigationTabs';
import VisitDetails from 'pages/Dashboard/pages/Encounters/pages/List/components/PatientDetails/VisitDetails';
import {
  PatientMetaDataProps,
} from 'pages/Dashboard/pages/Encounters/pages/List/types/patientMetaData';
import Info from 'pages/Dashboard/pages/Encounters/pages/List/components/PatientDetails/Info';
import {
  EncounterViewModel,
  InsuranceDTO,
} from 'dtos';
import {
  Link,
} from 'react-router-dom';
import NotesIcon from 'core/icons/notes';
import {
  useMixpanelTrack,
} from 'core/hooks';
import MediaContents from 'pages/Dashboard/pages/Encounters/pages/List/components/PatientDetails/MediaContents';
import PastNotes from 'pages/Dashboard/pages/Encounters/pages/List/components/PatientDetails/PastNotes';
import JoinTelehealthVisitButton from 'pages/Dashboard/pages/Appointments/pages/List/components/JoinTelehealthVisitButton';

const defaultTabs = ['info', 'visitDetails', 'notes', 'imaging', 'report'];
export type PatientDetailsTab = typeof defaultTabs[number];

type Section = {
  key: PatientDetailsTab;
  label: string;
  content: FC<PatientMetaDataProps>;
};

const sectionList: Section[] = [{
  key: 'info',
  label: 'Information',
  content: Info,
}, {
  key: 'visitDetails',
  label: 'Visit Details',
  content: VisitDetails,
}, {
  key: 'notes',
  label: 'Notes',
  content: MediaContents,
}, {
  key: 'pastNotes',
  label: 'Past Visits',
  content: PastNotes,
}, {
  key: 'imaging',
  label: 'Imaging',
  content: MediaContents,
}, {
  key: 'report',
  label: 'Report',
  content: MediaContents,
}];

type Props = {
  className?: string;
  encounter: EncounterViewModel;
  insurance?: InsuranceDTO;
  isOutOfPocket?: boolean;
  tabs?: PatientDetailsTab[];
  hasLink?: boolean;
};

export default function PatientDetails({
  className,
  tabs = defaultTabs,
  hasLink = false,
  encounter,
}: Props) {
  const mixPanelTrack = useMixpanelTrack();
  const tabSet = new Set(tabs);
  const sections = useMemo(
    () => sectionList.filter(
      (section) => tabSet.has(section.key),
    ),
    [tabs],
  );

  const [activeButtonIndex, setActiveButtonIndex] = useState<number>(0);
  const Component = useMemo<FC<PatientMetaDataProps>>(
    () => sections[activeButtonIndex].content,
    [sections, activeButtonIndex],
  );

  const trackTabClick = (index: number) => {
    const item = sections[index].key;
    const tabName = item.charAt(0).toUpperCase() + item.slice(1);
    mixPanelTrack(`ClickedEncounterTab${tabName}`);
  };

  return (
    <div className={clsx('flex flex-col h-full w-full overflow-y-hidden gap-2', className)}>
      <div className="flex justify-between items-center px-4 gap-2">
        <NavigationTabs
          className="flex !min-h-[2rem] relative min-w-0"
          classes={{ tab: '!normal-case !w-fit !min-w-[unset]' }}
          tabs={sections}
          activeIndex={activeButtonIndex}
          onClick={(index) => {
            setActiveButtonIndex(index);
            trackTabClick(index);
          }}
        />
        {hasLink && (
          <div className="flex items-center gap-2 shrink-0">
            <JoinTelehealthVisitButton
              encounter={encounter}
            />
            <Link
              to={`/encounters/${encounter.encounterId}`}
              className="border rounded !border-blue-500 font-semibold !text-xs py-2 pl-2 pr-2.5 gap-1 flex items-center text-blue-500 nw-visit-note !w-fit"
            >
              <NotesIcon className="!h-4 !w-4 !text-inherit" />
              Visit Note
            </Link>
          </div>
        )}
      </div>
      <div className="flex flex-1 flex-col overflow-auto">
        <Component encounter={encounter} type={sections[activeButtonIndex].key} />
      </div>
    </div>
  );
}
